import { createApi } from '@reduxjs/toolkit/query/react';
import JSONbig from 'json-bigint';

import {
  LeadsEndpoints as Endpoints,
  type AssignLeadsToUserBySearchIdRequest,
  type AssignLeadsToUserBySearchIdResponse,
  type AssignLeadsToUserRequest,
  type AssignLeadsToUserResponse,
  type CorrectLeadsFirstChannelRequest,
  type CorrectLeadsFirstChannelResponse,
  type DeleteLeadsBySearchIdRequest,
  type DeleteLeadsBySearchIdResponse,
  type DeleteLeadsGroupsRequest,
  type DeleteLeadsGroupsResponse,
  type DeleteLeadsRequest,
  type DeleteLeadsResponse,
  type FastSearchLeadsRequest,
  type FastSearchLeadsResponse,
  type FreeLeadResponse,
  type GetAutoReplyForClientRequest,
  type GetAutoReplyForClientResponse,
  type GetRecordSlotForClientRequest,
  type GetRecordSlotForClientResponse,
  type GetClientLeadForViewRequest,
  type GetClientLeadForViewResponse,
  type GetLeadByIdRequest,
  type GetLeadByIdResponse,
  type GetLeadGroupsMetricsRequest,
  type GetLeadGroupsMetricsResponse,
  type GetLeadsBySearchIdRequest,
  type GetLeadsBySearchIdResponse,
  type GetLeadsFilterDataRequest,
  type GetLeadsFilterDataResponse,
  type MarkLeadAsReadRequest,
  type MarkLeadAsReadResponse,
  type MarkLeadsAsAnsweredRequest,
  type MarkLeadsAsAnsweredResponse,
  type MarkLeadsAsAnsweredBySearchIdRequest,
  type MarkLeadsAsAnsweredBySearchIdResponse,
  type MarkLeadsAsSpamRequest,
  type MarkLeadsAsSpamResponse,
  type MarkLeadsAsUnreadRequest,
  type MarkLeadsAsUnreadResponse,
  type SearchLeadsByPhoneRequest,
  type SearchLeadsByPhoneResponse,
  type SearchLeadsRequest,
  type SearchLeadsResponse,
  type RemoveLeadsTagsRequest,
  type RemoveLeadsTagsResponse,
  type RemoveLeadsTagsBySearchIdRequest,
  type RemoveLeadsTagsBySearchIdResponse,
  type SetLeadsTagsBySearchIdRequest,
  type SetLeadsTagsBySearchIdResponse,
  type SetLeadsTagsRequest,
  type SetLeadsTagsResponse,
  type TakeLeadResponse,
  type TakeOrFreeLeadRequest,
} from '@app/api/Leads/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { LEADS_CHUNK } from '@app/constants';

export const leadsApi3 = createApi({
  reducerPath: 'leadsApi3',
  baseQuery: createApiBaseQuery(LEADS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetLeadsFilterData]: query<
      GetLeadsFilterDataResponse,
      GetLeadsFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadsFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetLeadById]: query<GetLeadByIdResponse, GetLeadByIdRequest>({
      query: (params) => ({
        url: Endpoints.GetLeadById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.MarkLeadsAsSpam]: mutation<
      MarkLeadsAsSpamResponse,
      MarkLeadsAsSpamRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.MarkLeadsAsSpam,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.MarkLeadsAsAnswered]: mutation<
      MarkLeadsAsAnsweredResponse,
      MarkLeadsAsAnsweredRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.MarkLeadsAsAnswered,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.MarkLeadsAsAnsweredBySearchId]: mutation<
      MarkLeadsAsAnsweredBySearchIdResponse,
      MarkLeadsAsAnsweredBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.MarkLeadsAsAnsweredBySearchId,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.MarkLeadsAsUnread]: mutation<
      MarkLeadsAsUnreadResponse,
      MarkLeadsAsUnreadRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.MarkLeadsAsUnread,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.MarkLeadAsRead]: mutation<
      MarkLeadAsReadResponse,
      MarkLeadAsReadRequest
    >({
      query: (params) => ({
        url: Endpoints.MarkLeadAsRead,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.AssignLeadsToUser]: mutation<
      AssignLeadsToUserResponse,
      AssignLeadsToUserRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AssignLeadsToUser,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.AssignLeadsToUserBySearchId]: mutation<
      AssignLeadsToUserBySearchIdResponse,
      AssignLeadsToUserBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.AssignLeadsToUserBySearchId,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.SearchLeads]: mutation<SearchLeadsResponse, SearchLeadsRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchLeads,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.FastSearchLeads]: mutation<
      FastSearchLeadsResponse,
      FastSearchLeadsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.FastSearchLeads,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetLeadsTags]: mutation<
      SetLeadsTagsResponse,
      SetLeadsTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetLeadsTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.RemoveLeadsTags]: mutation<
      RemoveLeadsTagsResponse,
      RemoveLeadsTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.RemoveLeadsTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetLeadsTagsBySearchId]: mutation<
      SetLeadsTagsBySearchIdResponse,
      SetLeadsTagsBySearchIdRequest
    >({
      // По контракту `SearchId` — int64 в теле запроса. `JSON.stringify` не
      // умеет сериализовать такие id из JS number без потери точности
      // (>2^53), поэтому тело собираем json-bigint: он печатает BigInt
      // точным числом без кавычек. Готовую строку axios отправляет как есть.
      query: ({ data, ...params }) => ({
        url: Endpoints.SetLeadsTagsBySearchId,
        method: Methods.POST,
        params,
        data: JSONbig.stringify({
          ...data,
          SearchId: BigInt(data.SearchId),
        }),
      }),
    }),
    [Endpoints.RemoveLeadsTagsBySearchId]: mutation<
      RemoveLeadsTagsBySearchIdResponse,
      RemoveLeadsTagsBySearchIdRequest
    >({
      // Тело — тот же контракт, что и в SetLeadsTagsBySearchId: `SearchId` —
      // int64, `JSON.stringify` теряет точность на id >2^53, поэтому собираем
      // тело через json-bigint (BigInt печатается точным числом без кавычек).
      query: ({ data, ...params }) => ({
        url: Endpoints.RemoveLeadsTagsBySearchId,
        method: Methods.POST,
        params,
        data: JSONbig.stringify({
          ...data,
          SearchId: BigInt(data.SearchId),
        }),
      }),
    }),
    [Endpoints.DeleteLeads]: mutation<DeleteLeadsResponse, DeleteLeadsRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteLeads,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteLeadsBySearchId]: mutation<
      DeleteLeadsBySearchIdResponse,
      DeleteLeadsBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteLeadsBySearchId,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.TakeLead]: mutation<TakeLeadResponse, TakeOrFreeLeadRequest>({
      query: (params) => ({
        url: Endpoints.TakeLead,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.FreeLead]: mutation<FreeLeadResponse, TakeOrFreeLeadRequest>({
      query: (params) => ({
        url: Endpoints.FreeLead,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetClientLeadForView]: query<
      GetClientLeadForViewResponse,
      GetClientLeadForViewRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientLeadForView,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetAutoReplyForClient]: query<
      GetAutoReplyForClientResponse,
      GetAutoReplyForClientRequest
    >({
      query: (params) => ({
        url: Endpoints.GetAutoReplyForClient,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordSlotForClient]: query<
      GetRecordSlotForClientResponse,
      GetRecordSlotForClientRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordSlotForClient,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetLeadsBySearchId]: query<
      GetLeadsBySearchIdResponse,
      GetLeadsBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadsBySearchId,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetLeadGroupsMetrics]: query<
      GetLeadGroupsMetricsResponse,
      GetLeadGroupsMetricsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadGroupsMetrics,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CorrectLeadsFirstChannel]: mutation<
      CorrectLeadsFirstChannelResponse,
      CorrectLeadsFirstChannelRequest
    >({
      query: (params) => ({
        url: Endpoints.CorrectLeadsFirstChannel,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.DeleteLeadsGroups]: mutation<
      DeleteLeadsGroupsResponse,
      DeleteLeadsGroupsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteLeadsGroups,
        method: Methods.DELETE,
        params,
        data,
      }),
    }),
    [Endpoints.SearchLeadsByPhone]: query<
      SearchLeadsByPhoneResponse,
      SearchLeadsByPhoneRequest
    >({
      query: (params) => ({
        url: Endpoints.SearchLeadsByPhone,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetLeadsFilterDataQuery,
  useLazyGetLeadsFilterDataQuery,
  useGetLeadByIdQuery,
  useLazyGetLeadByIdQuery,
  useMarkLeadsAsSpamMutation,
  useMarkLeadsAsAnsweredMutation,
  useMarkLeadsAsAnsweredBySearchIdMutation,
  useMarkLeadsAsUnreadMutation,
  useMarkLeadAsReadMutation,
  useAssignLeadsToUserMutation,
  useAssignLeadsToUserBySearchIdMutation,
  useSearchLeadsMutation,
  useFastSearchLeadsMutation,
  useSetLeadsTagsMutation,
  useRemoveLeadsTagsMutation,
  useSetLeadsTagsBySearchIdMutation,
  useRemoveLeadsTagsBySearchIdMutation,
  useDeleteLeadsMutation,
  useDeleteLeadsBySearchIdMutation,
  useTakeLeadMutation,
  useFreeLeadMutation,
  useGetClientLeadForViewQuery,
  useLazyGetClientLeadForViewQuery,
  useGetLeadsBySearchIdQuery,
  useLazyGetLeadsBySearchIdQuery,
  useGetLeadGroupsMetricsQuery,
  useLazyGetLeadGroupsMetricsQuery,
  useCorrectLeadsFirstChannelMutation,
  useDeleteLeadsGroupsMutation,
  useSearchLeadsByPhoneQuery,
  useLazySearchLeadsByPhoneQuery,
  useLazyGetAutoReplyForClientQuery,
  useLazyGetRecordSlotForClientQuery,
} = leadsApi3;
